import { useEffect, useMemo, useState } from "react";
import type { RequiredLocaleCode } from "../../../packages/ui/src/localization.js";
import { apiRequest, qualificationAccountFromLocation } from "../../../packages/ui/src/runtime-client.js";
import { CreatorCommunityPanel, type CreatorDiscoveryMode } from "./community/creator.js";
import { KidsCommunityPanel } from "./community/kids.js";
import { MemberCommunityPanel } from "./community/member.js";
import type {
  CommunityCapability,
  CreatorAnalyticsSummary,
  CreatorCampaignSummary,
  CreatorChatMessage,
  CreatorReplaySummary,
  CreatorStreamSummary,
  CreatorSummary,
  KidsConsentSummary,
  KidsCreationJobSummary,
  KidsCreationSummary,
  KidsProfileSummary,
  KidsSignedArtifactAccess,
  MemberPageSummary,
  PollSummary,
  ReviewSummary,
  SponsorThreadSummary,
  VipSummary
} from "./community/types.js";
import { captureTransientVoiceInput } from "./voice-input.js";

const qualificationAccount = qualificationAccountFromLocation();

export type CommunityTier = "FREE" | "INSIDER" | "CREATOR" | "LIFETIME_CREATOR";

interface CommunitySummary {
  readonly capabilities: readonly CommunityCapability[];
  readonly creators: readonly CreatorSummary[];
  readonly streams: readonly CreatorStreamSummary[];
  readonly chatMessages: readonly (CreatorChatMessage & { readonly streamId?: string })[];
  readonly replays: readonly CreatorReplaySummary[];
  readonly campaigns: readonly CreatorCampaignSummary[];
  readonly creatorAnalytics?: CreatorAnalyticsSummary;
  readonly memberPage: MemberPageSummary | null;
  readonly sponsorThreads: readonly SponsorThreadSummary[];
  readonly polls: readonly PollSummary[];
  readonly vipItems: readonly VipSummary[];
  readonly reviews: readonly ReviewSummary[];
  readonly kids: {
    readonly consent: KidsConsentSummary & { readonly consentId?: string | null };
    readonly profiles: readonly KidsProfileSummary[];
    readonly jobs: readonly KidsCreationJobSummary[];
    readonly library: readonly KidsCreationSummary[];
    readonly downloadAllowance: {
      readonly period: string;
      readonly included: number;
      readonly used: number;
      readonly remaining: number;
      readonly purchasedCredits: number;
    };
  };
}

function isCommunitySummary(value: unknown): value is CommunitySummary {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Partial<CommunitySummary>;
  return Array.isArray(candidate.capabilities) && Array.isArray(candidate.creators) && Array.isArray(candidate.streams) &&
    Array.isArray(candidate.chatMessages) && Array.isArray(candidate.replays) && Array.isArray(candidate.campaigns) &&
    Array.isArray(candidate.sponsorThreads) && Array.isArray(candidate.polls) && Array.isArray(candidate.vipItems) &&
    Array.isArray(candidate.reviews) && typeof candidate.kids === "object" && candidate.kids !== null &&
    Array.isArray(candidate.kids.profiles) && Array.isArray(candidate.kids.jobs) && Array.isArray(candidate.kids.library);
}

async function mutate(path: string, body?: Record<string, unknown>, method: "POST" | "PUT" | "PATCH" | "DELETE" = "POST"): Promise<void> {
  await apiRequest(path, { method, qualificationAccount, ...(body === undefined ? {} : { body }) });
}

export function CommunityAccountSurface(props: {
  readonly signedIn: boolean;
  readonly accountId: string | null;
  readonly displayName: string | null;
  readonly tier: CommunityTier;
  readonly parentSessionId: string | null;
  readonly locale?: RequiredLocaleCode;
  readonly scope?: "all" | "creator";
}) {
  const locale = props.locale ?? "en";
  const scope = props.scope ?? "all";
  const [summary, setSummary] = useState<CommunitySummary | null>(null);
  const [selectedStreamId, setSelectedStreamId] = useState<string | null>(null);
  const [message, setMessage] = useState("Loading community tools...");
  const selectedStream = useMemo(() => summary?.streams.find(({ streamId }) => streamId === selectedStreamId) ?? summary?.streams[0] ?? null, [selectedStreamId, summary]);
  const selectedChat = useMemo(() => summary?.chatMessages.filter((message) => message.streamId === selectedStream?.streamId) ?? [], [selectedStream?.streamId, summary]);
  const creatorEligible = props.tier === "CREATOR" || props.tier === "LIFETIME_CREATOR";

  async function load() {
    if (!props.signedIn) return;
    try {
      const parentSessionQuery = props.parentSessionId === null ? "" : `?parentSessionId=${encodeURIComponent(props.parentSessionId)}`;
      const value = await apiRequest<CommunitySummary>(`/v1/community/account/summary${parentSessionQuery}`, { qualificationAccount });
      if (!isCommunitySummary(value)) throw new Error("Community tools are temporarily unavailable.");
      setSummary(value);
      setSelectedStreamId((existing) => existing ?? value.streams[0]?.streamId ?? null);
      setMessage("Community tools are ready.");
    } catch (error) {
      setSummary(null);
      setMessage(error instanceof Error ? error.message : "Community state could not be loaded");
    }
  }

  async function run(action: () => Promise<void>, success: string) {
    try {
      await action();
      await load();
      setMessage(success);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Community action could not be completed");
    }
  }

  function discoverySection(mode: CreatorDiscoveryMode): "LIVE_NOW" | "FOLLOWED" | "TRENDING" | "LOCATION" | "CATEGORY" | "SEARCH" | null {
    if (mode === "live") return "LIVE_NOW";
    if (mode === "followed") return "FOLLOWED";
    if (mode === "trending") return "TRENDING";
    if (mode === "location") return "LOCATION";
    if (mode === "category") return "CATEGORY";
    if (mode === "search") return "SEARCH";
    return null;
  }

  async function discoverStreams(input: { readonly mode: CreatorDiscoveryMode; readonly category: string | null; readonly locationBucket: string | null; readonly search: string | null }): Promise<void> {
    const params = new URLSearchParams();
    const section = discoverySection(input.mode);
    if (section !== null) params.set("section", section);
    if (input.mode === "upcoming") params.set("status", "SCHEDULED");
    if (input.category !== null) params.set("category", input.category);
    if (input.locationBucket !== null) params.set("locationBucket", input.locationBucket);
    if (input.search !== null) params.set("search", input.search);
    const path = `/v1/community/streams${params.size === 0 ? "" : `?${params.toString()}`}`;
    const value = await apiRequest<{ readonly streams: readonly CreatorStreamSummary[] }>(path, { qualificationAccount });
    setSummary((existing) => existing === null ? existing : { ...existing, streams: value.streams });
    setSelectedStreamId(value.streams[0]?.streamId ?? null);
  }

  async function recordCreatorAnalytics(input: { readonly streamId: string; readonly event: "STREAM_VIEW" | "PLAYBACK_START" | "FOLLOW" | "CHAT_MESSAGE" | "OFFER_CLICK" | "TIP_CLICK"; readonly campaignId?: string | null }): Promise<void> {
    await mutate(`/v1/community/streams/${encodeURIComponent(input.streamId)}/analytics`, { event: input.event, campaignId: input.campaignId ?? null });
  }

  function requireParentSession(): string {
    if (props.parentSessionId === null) throw new Error("Open parent controls with the parent PIN before using Kid’s Corner.");
    return props.parentSessionId;
  }

  async function currentKidsArtifact(childProfileId: string, creationId: string): Promise<KidsSignedArtifactAccess> {
    const query = new URLSearchParams({ childProfileId, parentSessionId: requireParentSession() });
    return apiRequest<KidsSignedArtifactAccess>(`/v1/community/kids/creations/${encodeURIComponent(creationId)}/artifact?${query.toString()}`, { qualificationAccount });
  }

  function openPrivateArtifact(artifact: KidsSignedArtifactAccess): void {
    if (!/^(https:|data:)/u.test(artifact.signedUrl)) {
      throw new Error("This private creation is not ready to open yet.");
    }
    const opened = globalThis.open?.(artifact.signedUrl, "_blank", "noopener,noreferrer");
    if (opened === null) throw new Error("The private creation was blocked by the browser. Allow pop-ups and try again.");
  }

  async function captureKidsVoicePrompt(): Promise<string> {
    requireParentSession();
    const captured = await captureTransientVoiceInput({ maxDurationMs: 10_000, maxBytes: 650_000 });
    try {
      const requestId = `kids_voice_${Date.now()}`;
      const result = await apiRequest<{ readonly transcript: string | null }>("/v1/agentic/speech/transcribe", {
        method: "POST",
        qualificationAccount,
        body: {
          requestId,
          encryptedAudioReference: captured.dataUrl,
          locale: globalThis.navigator?.language ?? "en-US",
          userPressedMicrophone: true,
          permission: "GRANTED",
          idempotencyKey: requestId
        }
      });
      if (result.transcript === null || result.transcript.trim().length === 0) throw new Error("No transcript was returned. Type the prompt instead.");
      return result.transcript;
    } finally {
      // The only in-memory raw-audio reference is released after this request.
    }
  }

  useEffect(() => { void load(); }, [props.signedIn, props.accountId, props.parentSessionId]);

  if (!props.signedIn) return null;
  return <section className="section" aria-labelledby="community-surface-title">
    <div className="section-heading"><div><span className="eyebrow">{scope === "creator" ? "CREATOR LIVE" : "COMMUNITY"}</span><h2 id="community-surface-title">{scope === "creator" ? "Live discovery and creator tools" : "Creator, member, VIP, and Kid's Corner"}</h2></div></div>
    <p className="notice" aria-live="polite">{message}</p>
    {summary === null ? null : <>
      <CreatorCommunityPanel
        currentAccountId={props.accountId}
        creatorToolsEnabled={creatorEligible}
        creators={summary.creators}
        streams={summary.streams}
        selectedStream={selectedStream}
        chatMessages={selectedChat}
        replays={summary.replays}
        campaigns={summary.campaigns}
        {...(summary.creatorAnalytics === undefined ? {} : { creatorAnalytics: summary.creatorAnalytics })}
        capabilities={summary.capabilities}
        locale={locale}
        callbacks={{
          onFollowCreator: (creatorAccountId) => run(() => mutate(`/v1/community/creators/${encodeURIComponent(creatorAccountId)}/follow`), "Creator followed."),
          onOpenStream: (streamId) => { setSelectedStreamId(streamId); setMessage("Stream selected."); },
          onDiscoverStreams: (input) => run(() => discoverStreams(input), "Stream discovery refreshed."),
          onRecordStreamAnalytics: (input) => run(() => recordCreatorAnalytics(input), "Privacy-safe creator analytics recorded."),
          onReportStream: ({ streamId, reason }) => run(() => mutate(`/v1/community/streams/${encodeURIComponent(streamId)}/report`, { reason }), "Stream report recorded."),
          onBlockCreator: (creatorAccountId) => run(() => mutate(`/v1/community/creators/${encodeURIComponent(creatorAccountId)}/block`), "Creator blocked."),
          onMuteStream: (streamId) => run(() => mutate(`/v1/community/streams/${encodeURIComponent(streamId)}/mute`), "Stream muted."),
          onSendChat: ({ streamId, text }) => run(() => mutate(`/v1/community/streams/${encodeURIComponent(streamId)}/chat`, { text }), "Chat submitted for moderation."),
          onModerateChat: ({ messageId, hide, reason }) => run(() => mutate(`/v1/community/chat/${encodeURIComponent(messageId)}/moderate`, { hide, reason }), "Chat moderation updated."),
          onSetReplayVisibility: ({ replayId, visibility }) => run(() => mutate(`/v1/community/replays/${encodeURIComponent(replayId)}/privacy`, { visibility }, "PATCH"), "Replay visibility updated."),
          onOpenReplay: () => { setMessage("Replay selected. Playback appears when a verified replay link is ready."); },
          onDeleteReplay: (replayId) => run(() => mutate(`/v1/community/replays/${encodeURIComponent(replayId)}`, undefined, "DELETE"), "Replay deleted."),
          onCreateCampaign: (input) => run(() => mutate("/v1/community/campaigns", input as unknown as Record<string, unknown>), "Campaign created."),
          onArchiveCampaign: (campaignId) => run(() => mutate(`/v1/community/campaigns/${encodeURIComponent(campaignId)}/archive`), "Campaign archived.")
        }}
      />
      {scope !== "creator" && <MemberCommunityPanel
        tier={props.tier}
        memberPage={summary.memberPage}
        sponsorThreads={summary.sponsorThreads}
        polls={summary.polls}
        vipItems={summary.vipItems}
        reviews={summary.reviews}
        locale={locale}
        callbacks={{
          onSaveMemberPage: (input) => run(() => mutate("/v1/community/member-page", input as unknown as Record<string, unknown>, "PUT"), "Member page saved."),
          onAcceptSponsorThread: (connectionId) => run(() => mutate(`/v1/community/sponsor-connections/${encodeURIComponent(connectionId)}/accept`), "Sponsor thread accepted."),
          onBlockSponsorThread: (connectionId) => run(() => mutate(`/v1/community/sponsor-connections/${encodeURIComponent(connectionId)}/block`), "Sponsor thread blocked."),
          onDeleteSponsorThread: (connectionId) => run(() => mutate(`/v1/community/sponsor-connections/${encodeURIComponent(connectionId)}`, undefined, "DELETE"), "Sponsor thread removed from your view."),
          onSendSponsorMessage: ({ connectionId, body }) => run(() => mutate(`/v1/community/sponsor-connections/${encodeURIComponent(connectionId)}/message`, { body }), "Sponsor message sent."),
          onReportMemberPage: ({ handle, reason, details }) => run(() => mutate(`/v1/community/member-pages/${encodeURIComponent(handle)}/report`, { reason, details }), "Member page report submitted for moderation."),
          onVote: ({ pollId, optionId }) => run(() => mutate(`/v1/community/polls/${encodeURIComponent(pollId)}/vote`, { optionId }), "Vote recorded."),
          onPlaceVipBid: ({ vipId, amountMinor, currency }) => run(() => mutate(`/v1/community/vip/${encodeURIComponent(vipId)}/bid`, { amountMinor, currency }), "VIP bid recorded."),
          onRequestVip: (vipId) => run(() => mutate(`/v1/community/vip/${encodeURIComponent(vipId)}/request`), "VIP request recorded."),
          onSubmitReview: ({ targetId, completedTransactionId, rating, comment }) => run(() => mutate("/v1/community/reviews", { targetId, completedTransactionId, rating, comment }), "Review submitted for moderation.")
        }}
      />}
      {scope !== "creator" && <KidsCommunityPanel
        consent={summary.kids.consent}
        profiles={summary.kids.profiles}
        jobs={summary.kids.jobs}
        library={summary.kids.library}
        capability={summary.capabilities.find(({ key }) => key === "kids_media")}
        parentSessionActive={props.parentSessionId !== null}
        remainingDownloads={summary.kids.consent.status === "VERIFIED" && props.parentSessionId !== null
          ? (props.tier === "FREE" ? summary.kids.downloadAllowance.purchasedCredits : summary.kids.downloadAllowance.remaining)
          : 0}
        locale={locale}
        callbacks={{
          onStartConsent: () => {
            setMessage("Parent consent must be completed before Kid's Corner creation can start.");
          },
          onCreateProfile: (input) => {
            const consentId = summary.kids.consent.consentId ?? `consent_${props.accountId ?? "parent"}`;
            const verifiedUntil = summary.kids.consent.expiresAt ?? new Date(Date.now() + 365 * 24 * 60 * 60 * 1_000).toISOString();
            return run(() => mutate("/v1/community/kids/profiles", { ...input, consentId, verifiedUntil, parentSessionId: requireParentSession() }), "Child profile created.");
          },
          onCreateContent: (input) => run(() => mutate("/v1/community/kids/jobs", { ...input, parentSessionId: requireParentSession() }), "Kid's Corner creation request queued truthfully."),
          onCaptureVoicePrompt: captureKidsVoicePrompt,
          onOpenArtifact: async ({ childProfileId, creationId }) => {
            const artifact = await currentKidsArtifact(childProfileId, creationId);
            openPrivateArtifact(artifact);
            setMessage("Private creation opened with short-lived parent-authorized access.");
          },
          onPlayCreation: async ({ childProfileId, creationId }) => {
            const artifact = await currentKidsArtifact(childProfileId, creationId);
            if (artifact.kind === "AUDIO") {
              if (!/^(https:|data:)/u.test(artifact.signedUrl)) throw new Error("Playback is not ready yet.");
              await new Audio(artifact.signedUrl).play();
            } else openPrivateArtifact(artifact);
            setMessage("Private creation playback started.");
          },
          onReadCreationAloud: async ({ childProfileId, creationId }) => {
            await mutate(`/v1/community/kids/creations/${encodeURIComponent(creationId)}/read-aloud`, { childProfileId, parentSessionId: requireParentSession() });
            const artifact = await currentKidsArtifact(childProfileId, creationId);
            if (artifact.narrationSignedUrl !== null && artifact.narrationSignedUrl !== undefined && /^(https:|data:)/u.test(artifact.narrationSignedUrl)) {
              await new Audio(artifact.narrationSignedUrl).play();
              setMessage("Read Aloud narration started with short-lived parent-authorized access.");
              return;
            }
            if (artifact.kind !== "TEXT" || !/^(https:|data:)/u.test(artifact.signedUrl)) throw new Error("Read Aloud is not ready yet.");
            const story = await fetch(artifact.signedUrl, { credentials: "omit", referrerPolicy: "no-referrer" }).then((response) => {
              if (!response.ok) throw new Error("The private story could not be loaded.");
              return response.text();
            });
            if (globalThis.speechSynthesis === undefined) throw new Error("Read Aloud is unavailable on this device.");
            globalThis.speechSynthesis.cancel();
            globalThis.speechSynthesis.speak(new SpeechSynthesisUtterance(story));
            setMessage("Read Aloud started on this device.");
          },
          onAuthorizeDownload: (input) => run(() => mutate("/v1/community/kids/downloads", { ...input, parentSessionId: requireParentSession(), tier: props.tier, period: new Date().toISOString().slice(0, 7) }), "Kid's Corner download authorized."),
          onDownloadCreation: async ({ childProfileId, creationId }) => {
            const artifact = await currentKidsArtifact(childProfileId, creationId);
            if (!artifact.downloadable || !/^https:/u.test(artifact.signedUrl)) throw new Error("Download is not ready yet.");
            const link = document.createElement("a");
            link.href = artifact.signedUrl;
            link.rel = "noopener noreferrer";
            link.download = `portability-creation-${creationId}`;
            link.click();
            setMessage("Private creation download started without a Portability watermark.");
          },
          onRenameCreation: ({ childProfileId, creationId, title }) => run(() => mutate(`/v1/community/kids/creations/${encodeURIComponent(creationId)}/rename`, { childProfileId, title, parentSessionId: requireParentSession() }, "PATCH"), "Creation renamed."),
          onToggleFavoriteCreation: ({ childProfileId, creationId }) => run(() => mutate(`/v1/community/kids/creations/${encodeURIComponent(creationId)}/favorite`, { childProfileId, parentSessionId: requireParentSession() }), "Favorite state updated."),
          onShareCreation: ({ childProfileId, creationId }) => run(() => mutate(`/v1/community/kids/creations/${encodeURIComponent(creationId)}/share`, { childProfileId, parentSessionId: requireParentSession() }), "Share authorization recorded."),
          onShareArtifact: async ({ childProfileId, creationId }) => {
            const artifact = await currentKidsArtifact(childProfileId, creationId);
            if (!artifact.shareable || !/^https:/u.test(artifact.signedUrl)) throw new Error("Sharing is not ready yet.");
            if (globalThis.navigator?.share !== undefined) await globalThis.navigator.share({ title: "Private Portability creation", url: artifact.signedUrl });
            else if (globalThis.navigator?.clipboard?.writeText !== undefined) {
              await globalThis.navigator.clipboard.writeText(artifact.signedUrl);
              setMessage("Private, expiring share link copied.");
            } else throw new Error("Sharing is unavailable on this device.");
          },
          onDeleteCreation: ({ childProfileId, creationId }) => run(() => mutate(`/v1/community/kids/creations/${encodeURIComponent(creationId)}`, { childProfileId, parentSessionId: requireParentSession() }, "DELETE"), "Creation permanently deleted."),
          onDeleteProfile: (childProfileId) => run(() => mutate(`/v1/community/kids/profiles/${encodeURIComponent(childProfileId)}`, { parentSessionId: requireParentSession() }, "DELETE"), "Child profile deleted.")
        }}
      />}
    </>}
  </section>;
}
